/**
 * Collision primitives (GDD §4.1, §7, §8.1). Pure, allocation-free checks on squared distances.
 *
 * Touching counts as a collision (GDD §5.2): every comparison is `<=` with EPSILON, so exact contact in
 * the decimal numbers of the GDD survives binary rounding (0.1 + 0.2 is 0.30000000000000004).
 */
export const EPSILON = 1e-9;

/** Axis-aligned box given by its centre and half sizes. */
export interface Aabb {
  x: number;
  y: number;
  halfW: number;
  halfH: number;
}

export interface Circle {
  x: number;
  y: number;
  r: number;
}

const checkSize = (value: number, name: string) => {
  if (!Number.isFinite(value) || value < 0) throw new RangeError(`${name} must be a finite number >= 0, got ${value}`);
};

const checkPosition = (value: number, name: string) => {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite, got ${value}`);
};

/** Box from its centre and full width and height. */
export function createAabb(x: number, y: number, width: number, height: number): Aabb {
  checkPosition(x, 'x');
  checkPosition(y, 'y');
  checkSize(width, 'width');
  checkSize(height, 'height');
  return { x, y, halfW: width / 2, halfH: height / 2 };
}

export function createCircle(x: number, y: number, r: number): Circle {
  checkPosition(x, 'x');
  checkPosition(y, 'y');
  checkSize(r, 'radius');
  return { x, y, r };
}

export function aabbOverlap(a: Aabb, b: Aabb): boolean {
  return (
    Math.abs(a.x - b.x) <= a.halfW + b.halfW + EPSILON && Math.abs(a.y - b.y) <= a.halfH + b.halfH + EPSILON
  );
}

/** The circle overlaps the box when the closest point of the box is within the radius. */
export function circleAabbOverlap(c: Circle, a: Aabb): boolean {
  const dx = Math.max(Math.abs(c.x - a.x) - a.halfW, 0);
  const dy = Math.max(Math.abs(c.y - a.y) - a.halfH, 0);
  return dx * dx + dy * dy <= c.r * c.r + EPSILON;
}

export function circleOverlap(c: Circle, d: Circle): boolean {
  const dx = c.x - d.x;
  const dy = c.y - d.y;
  const r = c.r + d.r;
  return dx * dx + dy * dy <= r * r + EPSILON;
}
